/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { PageHeader } from '../components/PageHeader.js';
import { ReviewHistory, type PastReview } from '../components/regions/ReviewHistory.js';
import { inputStyle, monoLabel, today } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * Quarterly Review — the season-long reflection surface. Mirrors the
 * weekly review: a run form on top, a feed of past quarters below.
 */
export function QuarterlyReviewPage() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader label={t.nav.quarterlyReview} />

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 620,
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
          }}
        >
          <QuarterlyReview quarterEnd={today()} />
        </div>
      </div>
    </div>
  );
}

function QuarterlyReview({ quarterEnd }: { quarterEnd: string }) {
  const utils = trpc.useUtils();
  const run = trpc.quarterly.run.useMutation({
    onSuccess: async () => {
      await utils.quarterly.history.invalidate();
    },
  });
  const history = trpc.quarterly.history.useQuery();
  const [reflection, setReflection] = useState('');
  const [nextQuarterFocus, setNextQuarterFocus] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (reflection.trim() === '') return;
    run.mutate({
      quarterEnd,
      reflection: reflection.trim(),
      nextQuarterFocus: nextQuarterFocus.trim() === '' ? null : nextQuarterFocus.trim(),
    });
  };

  const result = run.data;

  const pastReviews: PastReview[] = (history.data ?? []).map((r) => ({
    id: r.id,
    periodEnd: r.quarterEnd,
    closingObservation: r.closingObservation,
    reviewMarkdown: r.reviewMarkdown,
    priorities: r.nextQuarterPriorities,
  }));

  return (
    <>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={monoLabel}>Quarterly review</div>

        {result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="serif" style={{ fontSize: 16, lineHeight: 1.55, color: p.fg }}>
              <ReactMarkdown>{result.reviewMarkdown}</ReactMarkdown>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={monoLabel}>Next quarter</span>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {result.nextQuarterPriorities.map((pr, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontFeatureSettings: 'var(--serif-feat)',
                      fontSize: 15,
                      color: p.fg,
                      marginBottom: 4,
                    }}
                  >
                    {pr}
                  </li>
                ))}
              </ol>
            </div>
            <p
              style={{
                fontFamily: 'var(--font-serif)',
                fontFeatureSettings: 'var(--serif-feat)',
                fontStyle: 'italic',
                fontSize: 15,
                color: p.muted,
                margin: 0,
              }}
            >
              {result.closingObservation}
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="How did the quarter go? Wins, and what slipped."
              aria-label="How did the quarter go?"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
            <textarea
              value={nextQuarterFocus}
              onChange={(e) => setNextQuarterFocus(e.target.value)}
              placeholder="What would make next quarter a success? (optional)"
              aria-label="What would make next quarter a success?"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
            />
            <button
              type="submit"
              disabled={run.isPending || reflection.trim() === ''}
              style={{
                alignSelf: 'flex-start',
                padding: '10px 18px',
                background: p.fg,
                color: p.bg,
                border: 'none',
                fontSize: 13,
                cursor: run.isPending || reflection.trim() === '' ? 'not-allowed' : 'pointer',
                opacity: run.isPending || reflection.trim() === '' ? 0.6 : 1,
              }}
            >
              {run.isPending ? 'Reviewing…' : 'Run quarterly review →'}
            </button>
          </form>
        )}
        {run.error ? (
          <span style={{ ...monoLabel, color: p.accent }}>{run.error.message}</span>
        ) : null}
      </section>

      <ReviewHistory
        label="Past reviews"
        prioritiesLabel="Next quarter"
        reviews={pastReviews}
        isPending={history.isPending}
      />
    </>
  );
}
